import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useEffect, useMemo, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import Api from '../api';
import {
  CREDENTIAL_MONTH_OPTIONS,
  GRADUATION_YEAR_HINT,
  LICENSED_SINCE_HINT,
  NOT_APPLICABLE_LABEL,
  NOT_APPLICABLE_VALUE,
  canOptOutOfFirstLicensed,
  getCredentialYearOptionsIncluding,
  getFirstCredentialPhrase,
  getLicensedSinceFieldLabel,
  validateEducationCredentials,
} from '../utils/educationCredentials';
import styles from './EducationCredentialsConfirmDialog.module.scss';

type HygienistLike = {
  firstName?: string | null;
  type?: number | null;
  country?: number | string | null;
  graduationYear?: number | string | null;
  firstLicensedYear?: number | string | null;
  firstLicensedMonth?: number | string | null;
};

type ConfirmEducationCredentialsResult = {
  graduationYear: number;
  firstLicensedYear: number | null;
  firstLicensedMonth: number | null;
  educationCredentialsConfirmedOn: string;
};

interface EducationCredentialsConfirmDialogProps {
  open: boolean;
  hygienist?: HygienistLike | null;
  onSaved?: (result: ConfirmEducationCredentialsResult) => void;
}

const persistUserAccountSnapshot = (result: ConfirmEducationCredentialsResult) => {
  try {
    const legacyUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
    const storedUser = JSON.parse(safeLocalStorage.getItem('userAccount') || 'null');
    const current = legacyUser || storedUser;

    if (!current?.hygienist) return;

    const updated = {
      ...current,
      hygienist: {
        ...current.hygienist,
        graduationYear: result.graduationYear,
        firstLicensedYear: result.firstLicensedYear,
        firstLicensedMonth: result.firstLicensedMonth,
        educationCredentialsConfirmedOn: result.educationCredentialsConfirmedOn,
      },
    };

    safeLocalStorage.setItem('userAccount', JSON.stringify(updated));
    (window as any)?.TempStars?.User?.setCurrentUser?.(updated);
  } catch {
    // Snapshot refresh is best-effort; the server remains the source of truth.
  }
};

const EducationCredentialsConfirmDialog: React.FC<EducationCredentialsConfirmDialogProps> = ({
  open,
  hygienist,
  onSaved,
}) => {
  // Per field on purpose: the pre-1970 exception must reopen only the field that owns the
  // stored legacy value, not offer that year in the other dropdown as well.
  const graduationYearOptions = useMemo(
    () => getCredentialYearOptionsIncluding(hygienist?.graduationYear),
    [hygienist?.graduationYear],
  );
  const firstLicensedYearOptions = useMemo(
    () => getCredentialYearOptionsIncluding(hygienist?.firstLicensedYear),
    [hygienist?.firstLicensedYear],
  );
  const [selectedGradYear, setSelectedGradYear] = useState<number | undefined>(undefined);
  const [selectedFirstLicensedYear, setSelectedFirstLicensedYear] = useState<number | undefined>(undefined);
  const [selectedFirstLicensedMonth, setSelectedFirstLicensedMonth] = useState<number | undefined>(undefined);
  const [firstLicensedNotApplicable, setFirstLicensedNotApplicable] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !hygienist) return;

    const storedGrad = Number(hygienist.graduationYear);
    setSelectedGradYear(
      Number.isFinite(storedGrad) && storedGrad > 0 ? storedGrad : graduationYearOptions[0],
    );

    const storedLicensed = Number(hygienist.firstLicensedYear);
    setSelectedFirstLicensedYear(
      Number.isFinite(storedLicensed) && storedLicensed > 0 ? storedLicensed : undefined,
    );

    const storedMonth = Number(hygienist.firstLicensedMonth);
    setSelectedFirstLicensedMonth(
      Number.isInteger(storedMonth) && storedMonth >= 1 && storedMonth <= 12 ? storedMonth : undefined,
    );

    setFirstLicensedNotApplicable(hygienist.type === 2);
    setError('');
    setSaving(false);
  }, [open, hygienist, graduationYearOptions]);

  const type = hygienist?.type;
  const firstName = hygienist?.firstName || '';
  const canOptOut = canOptOutOfFirstLicensed(type);
  const fieldLabel = getLicensedSinceFieldLabel(type, hygienist?.country);
  // Dental Admin / Manager has no licence or certificate at all - skip the question entirely.
  const hideFirstLicensed = type === 2;

  const handleSave = async () => {
    const validated = validateEducationCredentials({
      graduationYear: selectedGradYear,
      firstLicensedYear: firstLicensedNotApplicable ? null : selectedFirstLicensedYear,
      firstLicensedMonth: firstLicensedNotApplicable ? null : selectedFirstLicensedMonth,
      firstLicensedNotApplicable,
      allowNotApplicable: canOptOut,
    });

    if (!validated.ok) {
      setError(validated.message);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await Api.confirmEducationCredentials({
        graduationYear: validated.graduationYear,
        firstLicensedYear: validated.firstLicensedYear,
        firstLicensedMonth: validated.firstLicensedMonth,
        firstLicensedNotApplicable: validated.firstLicensedNotApplicable,
      });
      persistUserAccountSnapshot(result);
      onSaved?.(result);
    } catch (err: any) {
      const message = err?.message
        || err?.response?.data?.message
        || 'Could not save. Please try again.';
      setError(String(message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={() => { /* required - only dismiss after successful save */ }}
      disableBackdropClose
      disableEscapeKeyDown
      dataTestId="education-credentials-confirm-dialog"
      contentDesc="Confirm graduation and first licensed month and year"
      ariaLabel="Confirm education credentials"
      title={<b>Confirm Your Education Credentials</b> as any}
      content={(
        <div>
          <p className={styles.intro}>
            {hideFirstLicensed
              ? `Hi ${firstName}! Please confirm when you completed your dental education or training.`
              : `Hi ${firstName}! Please confirm when you graduated and when you were ${getFirstCredentialPhrase(type)}.`}
          </p>
          <label htmlFor="education-credentials-graduation-year" className={styles.fieldLabel}>
            Graduation Year
          </label>
          <select
            id="education-credentials-graduation-year"
            data-testid="education-credentials-graduation-year"
            content-desc="education-credentials-graduation-year"
            className={styles.select}
            value={selectedGradYear ?? ''}
            onChange={(e) => {
              setSelectedGradYear(Number(e.target.value));
              setError('');
            }}
            aria-label="Graduation Year"
            title="Select your graduation year"
          >
            {graduationYearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
          <p className={styles.hint}>{GRADUATION_YEAR_HINT}</p>

          {!hideFirstLicensed && (
            <>
              <span id="education-credentials-first-licensed-label" className={styles.fieldLabel}>
                {fieldLabel}
              </span>
              <div
                role="group"
                aria-labelledby="education-credentials-first-licensed-label"
                className={styles.row}
              >
                <select
                  id="education-credentials-first-licensed-month"
                  data-testid="education-credentials-first-licensed-month"
                  content-desc="education-credentials-first-licensed-month"
                  className={`${styles.select} ${styles.rowMonth}`}
                  data-empty={selectedFirstLicensedMonth || firstLicensedNotApplicable ? 'false' : 'true'}
                  value={firstLicensedNotApplicable ? NOT_APPLICABLE_VALUE : (selectedFirstLicensedMonth ?? '')}
                  onChange={(e) => {
                    const isNotApplicable = e.target.value === NOT_APPLICABLE_VALUE;
                    setFirstLicensedNotApplicable(isNotApplicable);
                    setSelectedFirstLicensedMonth(isNotApplicable ? undefined : Number(e.target.value));
                    if (isNotApplicable) {
                      setSelectedFirstLicensedYear(undefined);
                    }
                    setError('');
                  }}
                  aria-label={`${fieldLabel} - month`}
                  title={`${fieldLabel} - month`}
                >
                  <option value="" disabled>Month</option>
                  {CREDENTIAL_MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                  {canOptOut && (
                    <option
                      value={NOT_APPLICABLE_VALUE}
                      data-testid="education-credentials-na-option"
                    >
                      {NOT_APPLICABLE_LABEL}
                    </option>
                  )}
                </select>

                {!firstLicensedNotApplicable && (
                  <select
                    id="education-credentials-first-licensed-year"
                    data-testid="education-credentials-first-licensed-year"
                    content-desc="education-credentials-first-licensed-year"
                    className={`${styles.select} ${styles.rowYear}`}
                    data-empty={selectedFirstLicensedYear ? 'false' : 'true'}
                    value={selectedFirstLicensedYear ?? ''}
                    onChange={(e) => {
                      setSelectedFirstLicensedYear(Number(e.target.value));
                      setError('');
                    }}
                    aria-label={`${fieldLabel} - year`}
                    title={`${fieldLabel} - year`}
                  >
                    <option value="" disabled>Year</option>
                    {firstLicensedYearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
                  </select>
                )}
              </div>
              {!firstLicensedNotApplicable && <p className={styles.hint}>{LICENSED_SINCE_HINT}</p>}
            </>
          )}

          {error ? (
            <p
              id="education-credentials-error"
              data-testid="education-credentials-error"
              content-desc="education-credentials-error"
              className={styles.error}
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
      )}
      actions={[{
        label: saving ? 'Saving...' : 'Save',
        variant: 'contained',
        disabled: saving,
        onClick: handleSave,
      }]}
    />
  );
};

export default EducationCredentialsConfirmDialog;
